package org.teddapp.project.vault

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/** Paramètres de dérivation, stockés en clair à côté de la clé enveloppée (`keychain.kdf`). */
@Serializable
data class VaultKdf(
    val alg: String = "pbkdf2-sha256",
    val iter: Int,
    val salt: String,
    /** Vrai : pas de phrase secrète, la clé est enveloppée par un secret gardé sur l'appareil. */
    val auto: Boolean = false,
)

/** Ce qu'une entrée contient une fois ouverte. Chiffré en bloc dans `keychain_item.payload`. */
@Serializable
data class VaultEntry(
    val title: String = "",
    val username: String = "",
    val secret: String = "",
    val url: String = "",
    val notes: String = "",
    /** Bloc à recopier tel quel : une requête, une configuration, une commande. */
    val content: String = "",
    /** Format du bloc, une clé de [VaultFormat]. */
    val format: String = "",
)

/** Formats d'un bloc. Le format ne change que l'étiquette : le bloc est gardé tel qu'il a été collé. */
enum class VaultFormat(val key: String, val label: String) {
    Text("text", "Texte"),
    Json("json", "JSON"),
    Sql("sql", "SQL"),
    Shell("shell", "Shell"),
    Yaml("yaml", "YAML"),
    Env("env", ".env");

    companion object {
        fun of(key: String): VaultFormat = entries.firstOrNull { it.key == key } ?: Text

        private val SQL_START = Regex("^(select|insert|update|delete|create|alter|drop|with|grant|begin|explain)\\b", RegexOption.IGNORE_CASE)
        private val ENV_LINE = Regex("^[A-Z][A-Z0-9_]*=.*")
        private val YAML_LINE = Regex("^[A-Za-z_][\\w-]*:(\\s.*)?$")

        /** Devine le format d'un texte collé. Dans le doute : texte. */
        fun guess(text: String): VaultFormat {
            val t = text.trim()
            if (t.isEmpty()) return Text
            val lines = t.lines().map { it.trim() }.filter { it.isNotEmpty() && !it.startsWith("#") && !it.startsWith("--") }
            val first = lines.firstOrNull() ?: return Text
            return when {
                (t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]")) -> Json
                SQL_START.containsMatchIn(first) -> Sql
                t.startsWith("#!") || first.startsWith("$ ") || first.startsWith("sudo ") -> Shell
                lines.all { ENV_LINE.matches(it) } -> Env
                lines.size > 1 && lines.count { YAML_LINE.matches(it) || it.startsWith("- ") } >= lines.size * 2 / 3 -> Yaml
                else -> Text
            }
        }
    }
}

/** Un trousseau tout juste créé : ce qu'on écrit en base, et la clé à garder en mémoire. */
class NewVault(val wrappedKey: String, val kdf: String, val key: String)

/**
 * Le trousseau : une clé aléatoire chiffre les entrées ; elle est elle-même chiffrée par
 * une clé dérivée de la phrase secrète. Changer de phrase ne rechiffre donc que la clé.
 *
 * La phrase secrète n'est écrite nulle part. Perdue, le trousseau ne s'ouvre plus.
 */
object Vault {
    /** OWASP 2023 pour PBKDF2-HMAC-SHA256. */
    const val ITERATIONS = 600_000

    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }

    /**
     * Crée un trousseau. Avec [auto], [passphrase] est le secret de l'appareil, pas une
     * phrase tapée : le trousseau s'ouvrira sans rien demander sur cet appareil.
     */
    fun create(passphrase: String, iterations: Int = ITERATIONS, auto: Boolean = false): NewVault {
        val kdf = VaultKdf(iter = iterations, salt = VaultCipher.randomSalt(), auto = auto)
        val key = VaultCipher.newKey()
        val kek = VaultCipher.deriveKey(passphrase, kdf.salt, kdf.iter)
        return NewVault(
            wrappedKey = VaultCipher.seal(kek, key),
            kdf = json.encodeToString(VaultKdf.serializer(), kdf),
            key = key,
        )
    }

    /**
     * Réenveloppe la clé [key] d'un trousseau ouvert par une nouvelle phrase. Les entrées,
     * scellées par la clé, ne changent pas. Rend la clé enveloppée et les paramètres.
     */
    fun rewrap(key: String, passphrase: String, iterations: Int = ITERATIONS): Pair<String, String> {
        val kdf = VaultKdf(iter = iterations, salt = VaultCipher.randomSalt(), auto = false)
        val kek = VaultCipher.deriveKey(passphrase, kdf.salt, kdf.iter)
        return VaultCipher.seal(kek, key) to json.encodeToString(VaultKdf.serializer(), kdf)
    }

    /** Vrai si ce trousseau s'ouvre sans phrase, avec le secret de l'appareil. */
    fun isAuto(kdf: String): Boolean =
        runCatching { json.decodeFromString(VaultKdf.serializer(), kdf) }.getOrNull()?.auto == true

    /** La clé du trousseau, ou null si la phrase n'est pas la bonne. */
    fun unlock(passphrase: String, wrappedKey: String, kdf: String): String? {
        val params = runCatching { json.decodeFromString(VaultKdf.serializer(), kdf) }.getOrNull() ?: return null
        if (params.alg != "pbkdf2-sha256") return null
        return VaultCipher.open(VaultCipher.deriveKey(passphrase, params.salt, params.iter), wrappedKey)
    }

    fun sealEntry(key: String, entry: VaultEntry): String =
        VaultCipher.seal(key, json.encodeToString(VaultEntry.serializer(), entry))

    fun openEntry(key: String, payload: String): VaultEntry? =
        VaultCipher.open(key, payload)?.let { runCatching { json.decodeFromString(VaultEntry.serializer(), it) }.getOrNull() }
}
